import { useEffect, useState } from 'react';
import { calculateMileage, calculatePerDiemDay, fromCents } from '@d2p-expenses/shared';
import { api } from '../lib/api';
import { DesktopOnlyNotice, ErrorNote, Money, Spinner } from '../components/ui';

interface MileageRate {
  id: string;
  ratePerMile: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  note: string | null;
}

interface PerDiemRate {
  id: string;
  name: string;
  locality: string | null;
  breakfastAmount: string;
  lunchAmount: string;
  dinnerAmount: string;
  incidentalsAmount: string;
  firstLastDayPercent: string;
  singleDayPercent: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

export function AdminRatesPage() {
  const [mileage, setMileage] = useState<MileageRate[] | null>(null);
  const [perDiem, setPerDiem] = useState<PerDiemRate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    const [m, p] = await Promise.all([
      api.get<{ rates: MileageRate[] }>('/admin/mileage-rates'),
      api.get<{ rates: PerDiemRate[] }>('/admin/per-diem-rates'),
    ]);
    setMileage(m.rates);
    setPerDiem(p.rates);
  };

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : 'Could not load rates'));
  }, []);

  if (!mileage || !perDiem) return <Spinner label="Loading rates" />;

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h1 className="text-xl font-semibold">Rates</h1>
        <p className="mt-1 text-sm text-slate-600">
          Travel reports cannot be completed until both exist. Nothing is seeded by default —
          a plausible-looking wrong rate silently mis-reimburses every trip.
        </p>
      </div>
      <DesktopOnlyNotice />
      <ErrorNote>{error}</ErrorNote>
      {message ? <p className="text-sm text-emerald-700">{message}</p> : null}

      <MileageSection
        rates={mileage}
        onChanged={(m) => { setMessage(m); setError(null); void load(); }}
        onError={setError}
      />
      <PerDiemSection
        rates={perDiem}
        onChanged={(m) => { setMessage(m); setError(null); void load(); }}
        onError={setError}
      />
    </div>
  );
}

function MileageSection({
  rates, onChanged, onError,
}: {
  rates: MileageRate[];
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [rate, setRate] = useState('');
  const [from, setFrom] = useState(today());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const current = rates.find((r) => r.effectiveTo === null);

  // The same calculator the phone and the server use, so what an admin sees
  // here is exactly what a 100-mile trip will reimburse.
  const preview = (() => {
    if (!rate || Number(rate) <= 0) return null;
    try {
      return fromCents(calculateMileage({ miles: '100', ratePerMile: rate, roundTrip: false }).amount);
    } catch {
      return null;
    }
  })();

  async function add() {
    setBusy(true);
    try {
      const res = await api.post<{ closed: { effectiveTo: string } | null }>('/admin/mileage-rates', {
        ratePerMile: Number(rate).toFixed(4),
        effectiveFrom: from,
        note: note || undefined,
      });
      onChanged(
        res.closed
          ? `Rate added. The previous rate was closed on ${res.closed.effectiveTo}.`
          : 'Rate added.',
      );
      setRate('');
      setNote('');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not add the rate');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-4">
      <h2 className="font-semibold">Mileage</h2>
      <p className="mt-1 text-sm text-slate-600">
        {current
          ? <>Currently reimbursing <strong>${current.ratePerMile}</strong> per mile since {current.effectiveFrom}.</>
          : <span className="text-amber-700">No rate is configured — mileage cannot be claimed.</span>}
      </p>

      {rates.length > 0 ? (
        <table className="mt-3 w-full text-sm">
          <thead className="text-left text-slate-500">
            <tr><th className="py-1 font-medium">Rate</th><th className="font-medium">From</th><th className="font-medium">To</th><th className="font-medium">Note</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rates.map((r) => (
              <tr key={r.id}>
                <td className="py-1.5 tabular-nums">${r.ratePerMile}</td>
                <td>{r.effectiveFrom}</td>
                <td>{r.effectiveTo ?? <span className="text-emerald-700">current</span>}</td>
                <td className="text-slate-500">{r.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="mrate">Rate per mile</label>
          <input id="mrate" className="field" inputMode="decimal" placeholder="0.7150"
                 value={rate} onChange={(e) => setRate(e.target.value.replace(/[^\d.]/g, ''))} />
        </div>
        <div>
          <label className="label" htmlFor="mfrom">Effective from</label>
          <input id="mfrom" type="date" className="field" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="mnote">Note</label>
          <input id="mnote" className="field" placeholder="IRS 2027" value={note}
                 onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      {preview ? (
        <p className="mt-2 rounded-lg bg-brand-light px-3 py-2 text-sm text-brand">
          A 100-mile trip would reimburse <strong>${preview}</strong>.
        </p>
      ) : null}

      <p className="mt-2 text-xs text-slate-500">
        Adding a rate automatically closes the current one the day before, so there is never a
        gap or an overlap.
      </p>

      <button type="button" className="btn-primary mt-3" disabled={busy || !rate} onClick={() => void add()}>
        {busy ? 'Adding…' : 'Add rate'}
      </button>
    </section>
  );
}

function PerDiemSection({
  rates, onChanged, onError,
}: {
  rates: PerDiemRate[];
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [form, setForm] = useState({
    name: '', locality: '',
    breakfastAmount: '', lunchAmount: '', dinnerAmount: '', incidentalsAmount: '0.00',
    firstLastDayPercent: '0.75', singleDayPercent: '0.75',
    effectiveFrom: today(),
  });
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  // Mirrors the server exactly, including the order that matters: meals come
  // off at full value, then the day's percentage is applied.
  const preview = (() => {
    const { breakfastAmount, lunchAmount, dinnerAmount, incidentalsAmount } = form;
    if (!breakfastAmount || !lunchAmount || !dinnerAmount) return null;
    try {
      const rate = {
        breakfastAmount, lunchAmount, dinnerAmount,
        incidentalsAmount: incidentalsAmount || '0',
        firstLastDayPercent: Number(form.firstLastDayPercent).toFixed(4),
        singleDayPercent: Number(form.singleDayPercent).toFixed(4),
      };
      const claimed = { breakfastClaimed: true, lunchClaimed: true, dinnerClaimed: true, incidentalsClaimed: true };
      return {
        full: fromCents(calculatePerDiemDay({ dayType: 'full', ...claimed }, rate).amount),
        partial: fromCents(calculatePerDiemDay({ dayType: 'first_day', ...claimed }, rate).amount),
        noLunch: fromCents(
          calculatePerDiemDay({ dayType: 'full', ...claimed, lunchClaimed: false }, rate).amount,
        ),
      };
    } catch {
      return null;
    }
  })();

  async function add() {
    setBusy(true);
    try {
      await api.post('/admin/per-diem-rates', {
        name: form.name,
        locality: form.locality || undefined,
        breakfastAmount: Number(form.breakfastAmount).toFixed(2),
        lunchAmount: Number(form.lunchAmount).toFixed(2),
        dinnerAmount: Number(form.dinnerAmount).toFixed(2),
        incidentalsAmount: Number(form.incidentalsAmount || 0).toFixed(2),
        firstLastDayPercent: Number(form.firstLastDayPercent).toFixed(4),
        singleDayPercent: Number(form.singleDayPercent).toFixed(4),
        effectiveFrom: form.effectiveFrom,
      });
      onChanged('Per-diem rate added.');
      setForm({ ...form, name: '', locality: '', breakfastAmount: '', lunchAmount: '', dinnerAmount: '' });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not add the rate');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-4">
      <h2 className="font-semibold">Per diem</h2>
      <p className="mt-1 text-sm text-slate-600">
        {rates.length > 0
          ? `${rates.length} rate${rates.length === 1 ? '' : 's'} configured.`
          : <span className="text-amber-700">None configured — per diem cannot be claimed.</span>}
      </p>

      {rates.length > 0 ? (
        <table className="mt-3 w-full text-sm">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1 font-medium">Name</th><th className="font-medium">B</th>
              <th className="font-medium">L</th><th className="font-medium">D</th>
              <th className="font-medium">Inc.</th><th className="font-medium">Partial day</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rates.map((r) => (
              <tr key={r.id}>
                <td className="py-1.5">{r.name}{r.locality ? <span className="text-slate-500"> · {r.locality}</span> : null}</td>
                <td className="tabular-nums">${r.breakfastAmount}</td>
                <td className="tabular-nums">${r.lunchAmount}</td>
                <td className="tabular-nums">${r.dinnerAmount}</td>
                <td className="tabular-nums">${r.incidentalsAmount}</td>
                <td className="tabular-nums">{Math.round(Number(r.firstLastDayPercent) * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="pname">Name</label>
          <input id="pname" className="field" placeholder="CONUS standard" value={form.name} onChange={set('name')} />
        </div>
        <div>
          <label className="label" htmlFor="ploc">Locality (optional)</label>
          <input id="ploc" className="field" placeholder="NYC / SF / Boston" value={form.locality} onChange={set('locality')} />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div><label className="label" htmlFor="pb">Breakfast</label>
          <input id="pb" className="field" inputMode="decimal" value={form.breakfastAmount} onChange={set('breakfastAmount')} /></div>
        <div><label className="label" htmlFor="pl">Lunch</label>
          <input id="pl" className="field" inputMode="decimal" value={form.lunchAmount} onChange={set('lunchAmount')} /></div>
        <div><label className="label" htmlFor="pd">Dinner</label>
          <input id="pd" className="field" inputMode="decimal" value={form.dinnerAmount} onChange={set('dinnerAmount')} /></div>
        <div><label className="label" htmlFor="pi">Incidentals</label>
          <input id="pi" className="field" inputMode="decimal" value={form.incidentalsAmount} onChange={set('incidentalsAmount')} /></div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="pf">First/last day</label>
          <input id="pf" className="field" inputMode="decimal" value={form.firstLastDayPercent} onChange={set('firstLastDayPercent')} />
          <p className="mt-1 text-xs text-slate-500">0.75 pays 75% on travel days</p>
        </div>
        <div>
          <label className="label" htmlFor="ps">Single day</label>
          <input id="ps" className="field" inputMode="decimal" value={form.singleDayPercent} onChange={set('singleDayPercent')} />
          <p className="mt-1 text-xs text-slate-500">0 if a day trip pays nothing</p>
        </div>
        <div>
          <label className="label" htmlFor="pe">Effective from</label>
          <input id="pe" type="date" className="field" value={form.effectiveFrom} onChange={set('effectiveFrom')} />
        </div>
      </div>

      {preview ? (
        <div className="mt-3 rounded-lg bg-brand-light px-3 py-2 text-sm text-brand">
          <p>Full day <strong><Money value={preview.full} /></strong> · travel day <strong><Money value={preview.partial} /></strong></p>
          <p className="mt-1 text-xs">
            A full day with lunch provided pays <Money value={preview.noLunch} /> — the meal comes off
            at full value before the percentage applies.
          </p>
        </div>
      ) : null}

      <button type="button" className="btn-primary mt-3" disabled={busy || !form.name || !form.dinnerAmount}
              onClick={() => void add()}>
        {busy ? 'Adding…' : 'Add per-diem rate'}
      </button>
    </section>
  );
}
